// SPEC-464 M2 — 图片 fit 拟合语义（object-fit 的渲染器等价实现）。
// 零依赖纯 TS；worker/web 同源 import（iconOptics.ts / layout.ts 先例）→ 渲染 =
// facts = 仲裁三方同源（spec M2.4 接地仲裁 SoT）。
//
// 语义（spec M2.2）：
//   cover   = 源像素域居中裁剪 — 等比放大铺满显示盒、超出部分对称裁掉（永不变形；
//             盒比例≈档比例 → crop≈0 自然无损，海报类非回归对照的数学根源）；
//   contain = 等比缩放居中 — 完整放入显示盒（永不变形，可能留白）；
//   fill    = 拉伸铺满（现状语义，会变形 — 仅显式声明/存量对象无 fit 字段时）。
//
// 变形仲裁（spec M2.4）：「变形」类像素宣称仅在 fit='fill'（含无 fit 字段的存量
// 拉伸渲染）且 |ln(srcAR/boxAR)| > AR_DIFF_LOG_THRESHOLD 时成立。阈值与 M4
// image_distortion 审计同常数 — 跨里程碑 SoT 一致。

export type ImageFit = 'cover' | 'contain' | 'fill';

/** 拟合几何：源像素裁剪域（Konva cropX/cropY/cropWidth/cropHeight 同域）+
 * 盒内目标绘制尺寸/偏移（contain 居中使用；cover/fill 偏移恒 0）。 */
export interface FitGeom {
  cropX: number;
  cropY: number;
  cropW: number;
  cropH: number;
  drawW: number;
  drawH: number;
  offsetX: number;
  offsetY: number;
}

/** 拟合几何计算（纯函数，永不 throw）。非法输入（非有限/≤0）→ null = 渲染面回退
 * 现状拉伸路径（off 面/存量对象 byte-identical 红线）。
 * cover：crop = 源居中裁剪域（draw = 盒全量）；contain：draw = 等比放入尺寸
 *（crop = 全源）、offset = 居中；fill：全 = 现状（crop 全源、draw 全盒、零偏移）。 */
export function fitGeom(
  fit: ImageFit | string | undefined,
  srcW: number, srcH: number, boxW: number, boxH: number,
): FitGeom | null {
  if (!Number.isFinite(srcW) || !Number.isFinite(srcH) || !Number.isFinite(boxW) || !Number.isFinite(boxH)) return null;
  if (srcW <= 0 || srcH <= 0 || boxW <= 0 || boxH <= 0) return null;
  if (fit === 'cover') {
    const scale = Math.max(boxW / srcW, boxH / srcH);
    const cropW = boxW / scale;
    const cropH = boxH / scale;
    return { cropX: (srcW - cropW) / 2, cropY: (srcH - cropH) / 2, cropW, cropH, drawW: boxW, drawH: boxH, offsetX: 0, offsetY: 0 };
  }
  if (fit === 'contain') {
    const scale = Math.min(boxW / srcW, boxH / srcH);
    const drawW = srcW * scale;
    const drawH = srcH * scale;
    return { cropX: 0, cropY: 0, cropW: srcW, cropH: srcH, drawW, drawH, offsetX: (boxW - drawW) / 2, offsetY: (boxH - drawH) / 2 };
  }
  // fill/未识别 = 现状拉伸（渲染面不挂 crop/offset props，byte-identical）
  return { cropX: 0, cropY: 0, cropW: srcW, cropH: srcH, drawW: boxW, drawH: boxH, offsetX: 0, offsetY: 0 };
}

/** |ln(srcAR/boxAR)| — 比例差对数距离（乘法缩放下对称，pickGenSize 同款度量）。
 * 非法输入 → null（仲裁保守不介入）。 */
export function logArDiff(srcW: number, srcH: number, boxW: number, boxH: number): number | null {
  if (!Number.isFinite(srcW) || !Number.isFinite(srcH) || !Number.isFinite(boxW) || !Number.isFinite(boxH)) return null;
  if (srcW <= 0 || srcH <= 0 || boxW <= 0 || boxH <= 0) return null;
  return Math.abs(Math.log((srcW / srcH) / (boxW / boxH)));
}

/** 「变形」类宣称阈值（spec M2.4 / M4.1 同常数）：|ln(srcAR/boxAR)| > 0.15 才算可感变形。 */
export const AR_DIFF_LOG_THRESHOLD = 0.15;

// SPEC-465 M2 — cover 裁剪可见率（超宽 banner 槽×生成档失配 → 主体裁切信号的客观数据面）。

/** 拟合后可见源像素占比（0..1）：cover = 裁剪域面积/源面积（<1 即有裁切）；
 * contain/fill = 1（源全可见：留白/拉伸）。SoT = fitGeom（渲染器同款数学，零第二套实现）。
 * 非法输入 → null（保守不介入，同 fitGeom 约定）。 */
export function visibleFraction(
  fit: ImageFit | string | undefined,
  srcW: number, srcH: number, boxW: number, boxH: number,
): number | null {
  const g = fitGeom(fit, srcW, srcH, boxW, boxH);
  if (!g) return null;
  const srcArea = srcW * srcH;
  if (!(srcArea > 0)) return null;
  const frac = (g.cropW * g.cropH) / srcArea;
  if (!Number.isFinite(frac)) return null;
  return Math.min(1, Math.max(0, frac));
}

/** 低可见率阈值（SPEC-465 M2）：visible <60% = 源图多数被裁，审查环可感知信号 —
 * 客观数据 ride rubric；主体是否真被裁由 VL 对照截图判定（引擎不做显著性计算）。 */
export const VISIBLE_FRACTION_LOW = 0.6;
